import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from './client'
import type {
  Tenant,
  TenantBackends,
  CreateTenantRequest,
  GeoGroup,
  CreateGeoGroupRequest,
  UserGrant,
  CreateGrantRequest,
  DeviceType,
  CreateDeviceTypeRequest,
  SupportGrant,
  CreateSupportGrantRequest,
  TenantError,
  ListResult,
  Device,
} from '@/types'

// ── Tenants ──────────────────────────────────────────────────────────────────

export function useTenants() {
  const api = useApiClient()
  return useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<ListResult<Tenant>>('/api/v1/tenants'),
  })
}

export function useCreateTenant() {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateTenantRequest) =>
      api.post<Tenant>('/api/v1/tenants', req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  })
}

export function useDeleteTenant() {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tenantId: string) =>
      api.del(`/api/v1/tenants/${tenantId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  })
}

export function useTenant(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api.get<Tenant>(`/api/v1/tenants/${tenantId}`),
    enabled: !!tenantId,
  })
}

export function usePatchTenantSettings(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<Tenant>(`/api/v1/tenants/${tenantId}/settings`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', tenantId] }),
  })
}

export function useTenantBackends(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['tenant-backends', tenantId],
    queryFn: () => api.get<TenantBackends>(`/api/v1/tenants/${tenantId}/backends`),
    enabled: !!tenantId,
    // Poll every 5 s while onboarding hasn't completed (no onboarded_at or error present)
    refetchInterval: (query) => {
      const d = query.state.data as TenantBackends | undefined
      if (!d) return 5000
      if (!d.onboarded_at && !d.onboarding_error) return 5000
      return false
    },
  })
}

export function useTriggerOnboard(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<TenantBackends>(`/api/v1/tenants/${tenantId}/onboard`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-backends', tenantId] }),
  })
}

// ── Grafana Dashboards ───────────────────────────────────────────────────────

export interface GrafanaDashboard {
  id: number
  uid: string
  title: string
  url: string
  type: string
  tags: string[]
  isStarred: boolean
  folderTitle?: string
}

export function useDashboards(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['dashboards', tenantId],
    queryFn: () => api.get<GrafanaDashboard[]>(`/api/v1/tenants/${tenantId}/dashboards`),
    enabled: !!tenantId,
  })
}

export function useDeleteDashboard(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uid: string) =>
      api.del(`/api/v1/tenants/${tenantId}/dashboards/${uid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboards', tenantId] }),
  })
}

// ── Geo Groups ───────────────────────────────────────────────────────────────

export function useGeoGroups(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['geo-groups', tenantId],
    queryFn: () =>
      api.get<ListResult<GeoGroup>>(`/api/v1/tenants/${tenantId}/geo-groups`),
    enabled: !!tenantId,
  })
}

export function useCreateGeoGroup(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateGeoGroupRequest) =>
      api.post<GeoGroup>(`/api/v1/tenants/${tenantId}/geo-groups`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['geo-groups', tenantId] }),
  })
}

export function useDeleteGeoGroup(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (groupId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/geo-groups/${groupId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['geo-groups', tenantId] }),
  })
}

export function useGeoGroupDevices(tenantId: string | null, groupId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['geo-group-devices', tenantId, groupId],
    queryFn: () =>
      api.get<ListResult<Device>>(`/api/v1/tenants/${tenantId}/geo-groups/${groupId}/devices`),
    enabled: !!tenantId && !!groupId,
  })
}

export function useAddDeviceToGeoGroup(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, deviceId }: { groupId: string; deviceId: string }) =>
      api.post(`/api/v1/tenants/${tenantId}/geo-groups/${groupId}/devices`, { device_id: deviceId }),
    onSuccess: (_d, { groupId }) => {
      qc.invalidateQueries({ queryKey: ['geo-group-devices', tenantId, groupId] })
    },
  })
}

export function useRemoveDeviceFromGeoGroup(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, deviceId }: { groupId: string; deviceId: string }) =>
      api.del(`/api/v1/tenants/${tenantId}/geo-groups/${groupId}/devices/${deviceId}`),
    onSuccess: (_d, { groupId }) => {
      qc.invalidateQueries({ queryKey: ['geo-group-devices', tenantId, groupId] })
    },
  })
}

// ── User Grants ───────────────────────────────────────────────────────────────

export function useGrants(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['grants', tenantId],
    queryFn: () =>
      api.get<ListResult<UserGrant>>(`/api/v1/tenants/${tenantId}/grants`),
    enabled: !!tenantId,
  })
}

export function useCreateGrant(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateGrantRequest) =>
      api.post<UserGrant>(`/api/v1/tenants/${tenantId}/grants`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grants', tenantId] }),
  })
}

export function useDeleteGrant(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (grantId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/grants/${grantId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grants', tenantId] }),
  })
}

// ── Device Types ─────────────────────────────────────────────────────────────

export function useDeviceTypes(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['device-types', tenantId],
    queryFn: () => api.get<ListResult<DeviceType>>(`/api/v1/tenants/${tenantId}/device-types`),
    enabled: !!tenantId,
  })
}

export function useCreateDeviceType(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateDeviceTypeRequest) =>
      api.post<DeviceType>(`/api/v1/tenants/${tenantId}/device-types`, req),
    onSuccess: (newType) => {
      // Immediately add to cache so the list updates without waiting for refetch.
      qc.setQueryData(['device-types', tenantId], (old: ListResult<DeviceType> | undefined) =>
        old ? { ...old, data: [...(old.data ?? []), newType] } : old
      )
      qc.invalidateQueries({ queryKey: ['device-types', tenantId] })
    },
  })
}

// ── Support Grants ────────────────────────────────────────────────────────────

export function useSupportGrants(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['support-grants', tenantId],
    queryFn: () => api.get<SupportGrant[]>(`/api/v1/tenants/${tenantId}/support-grants`),
    enabled: !!tenantId,
  })
}

export function useCreateSupportGrant(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateSupportGrantRequest) =>
      api.post<SupportGrant>(`/api/v1/tenants/${tenantId}/support-grants`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-grants', tenantId] }),
  })
}

export function useRevokeSupportGrant(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (grantId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/support-grants/${grantId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-grants', tenantId] }),
  })
}

// ── Platform-admin cross-tenant views ────────────────────────────────────────

export function useAllSupportGrants() {
  const api = useApiClient()
  return useQuery({
    queryKey: ['all-support-grants'],
    queryFn: () => api.get<SupportGrant[]>('/api/v1/admin/support-grants'),
  })
}

export function useRevokeAnySupportGrant() {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ tenantId, grantId }: { tenantId: string; grantId: string }) =>
      api.del(`/api/v1/tenants/${tenantId}/support-grants/${grantId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-support-grants'] }),
  })
}

export function useTenantErrors() {
  const api = useApiClient()
  return useQuery({
    queryKey: ['tenant-errors'],
    queryFn: () => api.get<TenantError[]>('/api/v1/admin/tenant-errors'),
    refetchInterval: 30_000,
  })
}
